import { Fragment, useEffect, useRef, useState } from "react";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { PDFDocument, PDFDict, PDFRawStream, PDFObject } from "pdf-lib";
import { GlassFoldUdBox, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { JsonViewer } from "src/modules/JobModule/JsonViewer";
import { message } from "./Message";
import { BsPostage } from "react-icons/bs";
import { attemptGETGPTS } from "./PDFUtils";

export function PDFInspector(props: { window: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const [lastDroppedFile, setLastDroppedFile] = useState<File | null>(null);
   const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);



   return (
      <AdvancedDragModal
         window={props.window}
         title="PDF Inspector 🦖"
         PosDefault={{ width: "500px", height: "750px", left: "50px", top: "75px" }}
         onClose={() => { windowDispatch(closeWindow(props.window.id)); }}
         subHeaderContent={
            <>
               <ViewButtonIcon onClick={async () => {
                  const coords = pdfDoc ? await attemptGETGPTS(pdfDoc) : null;
                  if (coords) {
                     message.info(`GPTS Coordinates found: ${coords.flat().join(", ")}`);
                  } else {
                     message.info("No GPTS Coordinates found.");
                  }

               }} ><BsPostage /></ViewButtonIcon>
               <ViewButtonIcon onClick={(() => {
                  console.log(pdfDoc);
               })} >Bug</ViewButtonIcon>
            </>
         }
      >
         <ADModalBody>
            <ADMDropArea onDrop={(e) => {
               function handlePDF(file: File) {
                  let fileReader = new FileReader()
                  fileReader.onload = function () {
                     PDFDocument.load(fileReader.result as ArrayBuffer).then(doc => {
                        setPdfDoc(doc);
                     });
                  }
                  fileReader.readAsArrayBuffer(file);
               }
               handlePDF(e[0]);
               setLastDroppedFile(e[0]);
            }} />
            PDF Inspector Content
            {lastDroppedFile && <p>Last dropped file: {lastDroppedFile.name}</p>}
            {pdfDoc?.context?.enumerateIndirectObjects().map(([ref, object], _index) => {
               return (
                  <Fragment key={ref.objectNumber}>
                     <h4>Object {ref.objectNumber} {ref.generationNumber}</h4>
                     <PDFObjectRenderer object={object} />
                  </Fragment>
               );
            })}
         </ADModalBody>
      </AdvancedDragModal>
   );
}

function ADMDropArea({ onDrop }: { onDrop: (files: FileList) => void }) {
   const divRef = useRef<HTMLDivElement>(null);

   return (
      <div
         ref={divRef}
         className="ADMDropArea"
         onDragOver={(e) => {
            e.preventDefault();
            if (e.dataTransfer.items && e.dataTransfer.items[0].kind === 'file') {
               if (divRef.current?.classList.contains('ADMDropArea-DragOver') === false)
                  divRef.current?.classList.add('ADMDropArea-DragOver');
            }
         }}
         onDragEnd={() => {
            if (divRef.current?.classList.contains('ADMDropArea-DragOver') === false)
               divRef.current?.classList.add('ADMDropArea-DragOver');
         }}
         onDrop={(e) => {
            e.preventDefault();
            onDrop(e.dataTransfer.files);
            if (divRef.current?.classList.contains('ADMDropArea-DragOver') === true)
               divRef.current?.classList.remove('ADMDropArea-DragOver');
         }}
      >
         <p>Drop PDF files here</p>
      </div>
   );
}

function PDFObjectRenderer(props: { object: PDFObject }) {

   if (props.object instanceof PDFDict) {
      return <PDFDictRenderer dict={props.object} />
   }

   if (props.object instanceof PDFRawStream) {
      return <PDFRawStreamRenderer stream={props.object} />
   }



   return (
      <button
         onClick={() => { console.log(props.object) }}
      >
         PDF Object
      </button>
   )
}

function PDFDictRenderer({ dict }: { dict: PDFDict }) {
   let jsonReturn = {};
   dict.entries().forEach(([key, value]) => {
      jsonReturn[key.toString()] = value.toString?.() || "Uknown";
   });
   return (
      <JsonViewer parsedJson={jsonReturn} level={0} />
   )
}

function PDFRawStreamRenderer({ stream }: { stream: PDFRawStream }) {
   let [data, setData] = useState<any>("");

   let jsonReturn = {};
   stream.dict.entries().forEach(([key, value]) => {
      jsonReturn[key.toString()] = value.toString?.() || "Uknown";
   });

   useEffect(() => {
      let isZipped = jsonReturn["/Filter"];
      // let type = jsonReturn["/Type"];
      let subType = jsonReturn["/Subtype"];

      DecompressArrayBuffer(stream.getContents() as any, isZipped === "/FlateDecode" ? true : false)
         .then(blob => {
            const reader = new FileReader();
            reader.onload = function () {
               if (subType === "/Image") {
                  ArrayBufferToImage(new Uint8Array(reader.result as ArrayBuffer), jsonReturn).then((url) => {
                     if (url) {
                        setData(<img src={url} alt="PDF Image" />);
                     } else {
                        setData("Could not render image.");
                     }
                  });
                  return;
               }
               setData(reader.result as string);
            }

            if (subType === "/Image") {
               reader.readAsArrayBuffer(blob);
               return;
            }
            reader.readAsText(blob);
         });


   }, [stream]);

   return (
      <>
         <JsonViewer parsedJson={jsonReturn} level={0} />
         <GlassFoldUdBox title={"Data"} foldetUd={false} >
            <p style={{
               lineBreak: "normal",
               whiteSpace: "pre"
            }}>{data}</p>
         </GlassFoldUdBox>
      </>
   )
}


async function DecompressArrayBuffer(arrBuffer: ArrayBufferLike, deflate?: boolean): Promise<Blob> {
   const ds = new DecompressionStream("deflate");
   const blob = new Blob([arrBuffer as any]);
   if (deflate === false) {
      return blob;
   }
   const decompressedStream = blob.stream().pipeThrough(ds);
   return await new Response(decompressedStream).blob();
}



async function ArrayBufferToImage(rawData: Uint8Array, info: any): Promise<string | undefined> {
   let height = info["/Height"] as number;
   let width = info["/Width"] as number;

   console.log(`Image found - Width: ${width}, Height: ${height}, Length: ${rawData.length}`);
   const canvas = document.createElement('canvas');
   canvas.width = width;
   canvas.height = height;
   const ctx = canvas.getContext('2d');
   // Add Aplpha to rawData
   const cSL = Math.round(rawData.length / (width * height));
   const realSize = width * height;

   const padding = (rawData.length - (realSize * cSL)) / height;
   const paddingPerRow = (rawData.length / height) - (width * cSL);
   console.log("Padding per row:", paddingPerRow);
   console.log("Padding:", padding);
   console.log("Color Space Length:", cSL);
   let rawDataWithAlpha = new Uint8ClampedArray((realSize) * 4);
   for (let i = paddingPerRow, j = 0; i < rawData.length; i += cSL, j += 4) {
      if (padding && i % (width * cSL + paddingPerRow) >= width * cSL) {
         i += paddingPerRow;
      }
      rawDataWithAlpha[j] = rawData[i]; // R
      if (cSL == 1) {
         rawDataWithAlpha[j + 1] = rawData[i]; // G
         rawDataWithAlpha[j + 2] = rawData[i];
         rawDataWithAlpha[j + 3] = 255;
      } else if (cSL == 3) {
         rawDataWithAlpha[j + 1] = rawData[i + 1]; // G
         rawDataWithAlpha[j + 2] = rawData[i + 2]; // B
         rawDataWithAlpha[j + 3] = 255;
      } else if (cSL == 4) {
         rawDataWithAlpha[j + 1] = rawData[i + 1]; // G
         rawDataWithAlpha[j + 2] = rawData[i + 2];
         rawDataWithAlpha[j + 3] = rawData[i + 3]; // A
      }
   }
   try {
      ctx?.putImageData(new ImageData(rawDataWithAlpha, width, height), 0, 0);
      return new Promise((resolve, _reject) => {
         canvas.toBlob((blob) => {
            if (!blob) {
               message.error("Could not create blob from PDF image data.");
               return;
            }
            const url = URL.createObjectURL(blob);
            resolve(url);
         }, "image/png");
      });
   } catch (error) {
      console.error("Error creating image from PDF data:", error);
   }
}